/**
 * MonteCarloSimulation — runs N iterations of the projection engine
 * with normally-distributed random assumptions (Box-Muller transform).
 * Shows risk cards, P10-P90 fan chart, and percentile table.
 *
 * All computation is client-side. No backend calls.
 */

import { useState, useCallback } from 'react'
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  Cell, ReferenceLine,
} from 'recharts'
import { Loader2, Play } from 'lucide-react'
import { cn } from '../../lib/utils'
import { project, type BaseActuals, type Assumptions, type ProjectionResult } from '../../services/projectionEngine'

interface CatalogEntry {
  assumption_key: string
  display_name: string
  unit: string
  default_value: number
  min_value: number
  max_value: number
}

interface MonteCarloProps {
  assumptions: Assumptions
  catalog: CatalogEntry[]
  baseActuals: BaseActuals
  className?: string
}

// ─── Result types ────────────────────────────────────────────────────────

interface MetricDistribution {
  values: number[]
  percentiles: Record<number, number>
  mean: number
}

interface MonteCarloResults {
  iterations: number
  netIncome: MetricDistribution
  endingCash: MetricDistribution
  workingCapital: MetricDistribution
  risk: {
    probNetLoss: number
    probNegativeCash: number
    probWCNegative: number
    valueAtRisk95: number
  }
  deterministicNetIncome: number
  deterministicCash: number
  deterministicWC: number
}

// ─── Math helpers ────────────────────────────────────────────────────────

/** Box-Muller transform for normal distribution */
function gaussianRandom(mean: number, stdDev: number): number {
  const u1 = Math.random()
  const u2 = Math.random()
  const z = Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2)
  return mean + z * stdDev
}

function getPercentiles(values: number[], pcts: number[]): Record<number, number> {
  const sorted = [...values].sort((a, b) => a - b)
  const result: Record<number, number> = {}
  for (const p of pcts) {
    const idx = Math.floor((p / 100) * sorted.length)
    result[p] = sorted[Math.min(idx, sorted.length - 1)]
  }
  return result
}

function buildDistribution(values: number[]): MetricDistribution {
  return {
    values,
    percentiles: getPercentiles(values, [5, 10, 25, 50, 75, 90]),
    mean: values.reduce((a, b) => a + b, 0) / values.length,
  }
}

// ─── Simulation runner ───────────────────────────────────────────────────

function runMonteCarlo(
  baseAssumptions: Assumptions,
  catalog: CatalogEntry[],
  actuals: BaseActuals,
  iterations: number,
): MonteCarloResults {
  const netIncomes: number[] = []
  const cashPositions: number[] = []
  const workingCapitals: number[] = []

  for (let i = 0; i < iterations; i++) {
    const randomized: Record<string, number> = { ...baseAssumptions as unknown as Record<string, number> }

    for (const entry of catalog) {
      const key = entry.assumption_key
      const base = randomized[key] ?? entry.default_value
      const range = entry.max_value - entry.min_value
      const stdDev = range * 0.1
      const val = gaussianRandom(base, stdDev)
      randomized[key] = Math.max(entry.min_value, Math.min(entry.max_value, val))
    }

    const result: ProjectionResult = project(actuals, randomized as unknown as Assumptions)
    netIncomes.push(result.pl.net_income)
    cashPositions.push(result.cf.ending_cash)
    workingCapitals.push(result.bs.working_capital)
  }

  const deterministic = project(actuals, baseAssumptions)

  return {
    iterations,
    netIncome: buildDistribution(netIncomes),
    endingCash: buildDistribution(cashPositions),
    workingCapital: buildDistribution(workingCapitals),
    risk: {
      probNetLoss: netIncomes.filter(v => v < 0).length / iterations,
      probNegativeCash: cashPositions.filter(v => v < 0).length / iterations,
      probWCNegative: workingCapitals.filter(v => v < 0).length / iterations,
      valueAtRisk95: getPercentiles(netIncomes, [5])[5],
    },
    deterministicNetIncome: deterministic.pl.net_income,
    deterministicCash: deterministic.cf.ending_cash,
    deterministicWC: deterministic.bs.working_capital,
  }
}

// ─── Format helpers ──────────────────────────────────────────────────────

function fmt(v: number): string {
  const abs = Math.abs(v)
  const sign = v < 0 ? '-' : ''
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(1)}M`
  if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(0)}K`
  return `${sign}$${abs.toFixed(0)}`
}

function fmtPct(v: number): string {
  return `${(v * 100).toFixed(1)}%`
}

function riskColor(prob: number): string {
  if (prob > 0.5) return '#EF4444'
  if (prob > 0.2) return '#F59E0B'
  return '#10B981'
}

function valueColor(v: number): string {
  if (v < 0) return '#EF4444'
  if (v === 0) return 'var(--text-hint, #475569)'
  return '#10B981'
}

// ─── Histogram builder ───────────────────────────────────────────────────

function createHistogram(values: number[], bins: number) {
  const min = Math.min(...values)
  const max = Math.max(...values)
  if (min === max) return [{ bin: fmt(min), count: values.length, rangeStart: min, rangeEnd: max }]
  const step = (max - min) / bins
  const buckets = Array.from({ length: bins }, (_, i) => ({
    bin: fmt(min + step * (i + 0.5)),
    count: 0,
    rangeStart: min + step * i,
    rangeEnd: min + step * (i + 1),
  }))
  for (const v of values) {
    const idx = Math.min(Math.floor((v - min) / step), bins - 1)
    buckets[idx].count++
  }
  return buckets
}

// ─── Component ───────────────────────────────────────────────────────────

const ITERATION_OPTIONS = [100, 500, 1_000, 5_000] as const

export function MonteCarloSimulation({ assumptions, catalog, baseActuals, className }: MonteCarloProps) {
  const [results, setResults] = useState<MonteCarloResults | null>(null)
  const [isRunning, setIsRunning] = useState(false)
  const [iterations, setIterations] = useState(1_000)

  const runSimulation = useCallback(() => {
    setIsRunning(true)
    requestAnimationFrame(() => {
      const r = runMonteCarlo(assumptions, catalog, baseActuals, iterations)
      setResults(r)
      setIsRunning(false)
    })
  }, [assumptions, catalog, baseActuals, iterations])

  return (
    <div className={cn('flex flex-col gap-5', className)}>
      {/* Header + controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <div className="h-1.5 w-1.5 rounded-full bg-purple-500" />
          <span className="text-[10px] uppercase tracking-[0.8px] text-[var(--text-muted,#64748B)]">
            Monte Carlo Simulation
          </span>
        </div>

        <div className="flex items-center gap-2.5">
          <select
            value={iterations}
            onChange={e => setIterations(Number(e.target.value))}
            className={cn(
              'appearance-none bg-[var(--bg-elevated,var(--bg-elevated))] border border-white/10',
              'rounded-md px-2.5 py-1.5 text-[11px]',
              'text-[var(--text-primary,#F0F2F5)]',
              'focus:outline-none focus:border-[var(--accent,var(--accent))]',
            )}
          >
            {ITERATION_OPTIONS.map(n => (
              <option key={n} value={n}>{n.toLocaleString()} runs</option>
            ))}
          </select>

          <button
            onClick={runSimulation}
            disabled={isRunning}
            className={cn(
              'flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-medium',
              'bg-purple-600 text-white',
              'hover:bg-purple-500 transition-colors',
              'disabled:opacity-50 disabled:cursor-not-allowed',
            )}
          >
            {isRunning ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Play className="h-3.5 w-3.5" />
            )}
            {isRunning ? 'Running...' : 'Run Simulation'}
          </button>
        </div>
      </div>

      {/* Results */}
      {!results && !isRunning && (
        <p className="text-[12px] text-[var(--text-hint,#475569)] text-center py-6">
          Click "Run Simulation" to generate probabilistic outcomes from {iterations.toLocaleString()} randomized scenarios.
        </p>
      )}

      {results && (
        <>
          {/* Risk Summary Cards */}
          <div className="grid grid-cols-4 gap-3">
            <RiskCard
              label="P(Net Loss)"
              value={fmtPct(results.risk.probNetLoss)}
              probability={results.risk.probNetLoss}
            />
            <RiskCard
              label="P(Cash < 0)"
              value={fmtPct(results.risk.probNegativeCash)}
              probability={results.risk.probNegativeCash}
            />
            <RiskCard
              label="Expected NI"
              value={fmt(results.netIncome.mean)}
              valueStyle={{ color: valueColor(results.netIncome.mean) }}
            />
            <RiskCard
              label="95% VaR"
              value={fmt(results.risk.valueAtRisk95)}
              valueStyle={{ color: valueColor(results.risk.valueAtRisk95) }}
              hint="5th percentile of net income"
            />
          </div>

          {/* Distribution Chart */}
          <DistributionChart
            label="Net Income Distribution"
            distribution={results.netIncome}
            deterministicValue={results.deterministicNetIncome}
          />

          {/* Percentile Range Bars */}
          <div>
            <p className="text-[10px] uppercase tracking-[0.8px] text-[var(--text-muted,#64748B)] mb-3">
              Outcome Ranges (P10 - P90)
            </p>
            <div className="flex flex-col gap-3">
              <RangeBar
                label="Net Income"
                percentiles={results.netIncome.percentiles}
                deterministicValue={results.deterministicNetIncome}
              />
              <RangeBar
                label="Ending Cash"
                percentiles={results.endingCash.percentiles}
                deterministicValue={results.deterministicCash}
              />
              <RangeBar
                label="Working Capital"
                percentiles={results.workingCapital.percentiles}
                deterministicValue={results.deterministicWC}
              />
            </div>
          </div>

          {/* Percentile Table */}
          <PercentileTable results={results} />

          {/* Iteration info */}
          <p className="text-[10px] text-[var(--text-hint,#475569)] text-right">
            Based on {results.iterations.toLocaleString()} simulations with normally-distributed assumption variations
          </p>
        </>
      )}
    </div>
  )
}

// ─── Sub-components ──────────────────────────────────────────────────────

function RiskCard({
  label, value, probability, valueStyle, hint,
}: {
  label: string
  value: string
  probability?: number
  valueStyle?: React.CSSProperties
  hint?: string
}) {
  const barColor = probability != null ? riskColor(probability) : undefined
  const barWidth = probability != null ? `${Math.min(probability * 100, 100)}%` : undefined

  return (
    <div className={cn(
      'rounded-lg border border-white/[0.06] bg-[var(--bg-elevated,var(--bg-elevated))]',
      'px-3 py-3 flex flex-col gap-1.5',
    )}>
      <span className="text-[10px] uppercase tracking-[0.6px] text-[var(--text-hint,#475569)]">
        {label}
      </span>
      <span
        className="text-[18px] font-semibold font-['JetBrains_Mono',monospace]"
        style={valueStyle ?? (barColor ? { color: barColor } : { color: 'var(--text-primary, #F0F2F5)' })}
      >
        {value}
      </span>
      {barWidth && (
        <div className="h-1.5 w-full rounded-full bg-white/[0.06] overflow-hidden">
          <div
            className="h-full rounded-full transition-all"
            style={{ width: barWidth, backgroundColor: barColor }}
          />
        </div>
      )}
      {hint && (
        <span className="text-[9px] text-[var(--text-hint,#475569)]">{hint}</span>
      )}
    </div>
  )
}

function DistributionChart({
  label, distribution, deterministicValue,
}: {
  label: string
  distribution: MetricDistribution
  deterministicValue: number
}) {
  const histogram = createHistogram(distribution.values, 25)
  const p10 = distribution.percentiles[10]
  const p50 = distribution.percentiles[50]
  const p90 = distribution.percentiles[90]

  return (
    <div>
      <p className="text-[10px] uppercase tracking-[0.8px] text-[var(--text-muted,#64748B)] mb-2">
        {label}
      </p>
      <div className={cn(
        'rounded-lg border border-white/[0.06] bg-[var(--bg-surface,var(--bg-surface))]',
        'px-2 pt-3 pb-1',
      )}>
        <ResponsiveContainer width="100%" height={180}>
          <BarChart data={histogram} margin={{ top: 5, right: 10, left: 10, bottom: 0 }}>
            <XAxis
              dataKey="bin"
              tick={{ fill: 'var(--text-muted, #64748B)', fontSize: 9 }}
              axisLine={{ stroke: 'rgba(0,0,0,0.06)' }}
              tickLine={false}
              interval="preserveStartEnd"
            />
            <YAxis hide />
            <Tooltip
              cursor={{ fill: 'rgba(0,0,0,0.04)' }}
              contentStyle={{
                background: 'var(--bg-elevated, var(--bg-elevated))',
                border: '1px solid var(--border-default, #1E3A5F)',
                borderRadius: 6,
                fontSize: 11,
                color: 'var(--text-primary, #F0F2F5)',
              }}
              formatter={(value: any) => [`${value} runs`, 'Frequency']}
              labelFormatter={(label: any) => `Range: ${label}`}
            />
            {/* P10/P50/P90 reference lines */}
            <ReferenceLine x={fmt(p10)} stroke="#F59E0B" strokeDasharray="4 2" strokeWidth={1} />
            <ReferenceLine x={fmt(p50)} stroke="var(--accent)" strokeDasharray="4 2" strokeWidth={1} />
            <ReferenceLine x={fmt(p90)} stroke="#10B981" strokeDasharray="4 2" strokeWidth={1} />
            {/* Deterministic reference */}
            <ReferenceLine x={fmt(deterministicValue)} stroke="#A855F7" strokeWidth={1.5} />
            <Bar dataKey="count" radius={[2, 2, 0, 0]}>
              {histogram.map((bucket, i) => {
                const mid = (bucket.rangeStart + bucket.rangeEnd) / 2
                const fill = mid < p10
                  ? '#EF444480'
                  : mid > p90
                    ? '#10B98180'
                    : mid < p50
                      ? '#F59E0B60'
                      : 'var(--accent)60'
                return <Cell key={i} fill={fill} />
              })}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
        {/* Legend */}
        <div className="flex items-center justify-center gap-4 pb-2 text-[9px] text-[var(--text-hint,#475569)]">
          <span className="flex items-center gap-1"><span className="w-2 h-0.5 bg-[#F59E0B] inline-block" /> P10</span>
          <span className="flex items-center gap-1"><span className="w-2 h-0.5 bg-[var(--accent)] inline-block" /> P50</span>
          <span className="flex items-center gap-1"><span className="w-2 h-0.5 bg-[#10B981] inline-block" /> P90</span>
          <span className="flex items-center gap-1"><span className="w-2.5 h-0.5 bg-[#A855F7] inline-block" /> Current</span>
        </div>
      </div>
    </div>
  )
}

function RangeBar({
  label, percentiles, deterministicValue,
}: {
  label: string
  percentiles: Record<number, number>
  deterministicValue: number
}) {
  const p10 = percentiles[10]
  const p25 = percentiles[25]
  const p50 = percentiles[50]
  const p75 = percentiles[75]
  const p90 = percentiles[90]
  const min = p10
  const max = p90
  const range = max - min || 1

  const pct = (v: number) => `${Math.max(0, Math.min(100, ((v - min) / range) * 100))}%`

  return (
    <div className={cn(
      'rounded-lg border border-white/[0.06] bg-[var(--bg-elevated,var(--bg-elevated))]',
      'px-4 py-3',
    )}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-[11px] text-[var(--text-secondary,#94A3B8)]">{label}</span>
        <span className="text-[10px] text-[var(--text-hint,#475569)]">
          {fmt(p10)} to {fmt(p90)}
        </span>
      </div>
      <div className="relative h-4 rounded-full bg-white/[0.04] overflow-hidden">
        {/* P25-P75 band */}
        <div
          className="absolute top-0 h-full rounded-full bg-[var(--accent)]/20"
          style={{ left: pct(p25), width: `${((p75 - p25) / range) * 100}%` }}
        />
        {/* P50 marker */}
        <div
          className="absolute top-0 h-full w-0.5 bg-[var(--accent)]"
          style={{ left: pct(p50) }}
        />
        {/* Deterministic marker */}
        <div
          className="absolute top-0 h-full w-0.5 bg-[#A855F7]"
          style={{ left: pct(Math.max(min, Math.min(max, deterministicValue))) }}
        />
      </div>
      <div className="flex items-center justify-between mt-1 text-[9px] text-[var(--text-hint,#475569)]">
        <span>P10: {fmt(p10)}</span>
        <span style={{ color: 'var(--accent)' }}>P50: {fmt(p50)}</span>
        <span>P90: {fmt(p90)}</span>
      </div>
    </div>
  )
}

function PercentileTable({ results }: { results: MonteCarloResults }) {
  const pctLabels = [10, 25, 50, 75, 90] as const
  const metrics = [
    { label: 'Net Income', dist: results.netIncome, det: results.deterministicNetIncome },
    { label: 'Ending Cash', dist: results.endingCash, det: results.deterministicCash },
    { label: 'Working Capital', dist: results.workingCapital, det: results.deterministicWC },
  ]

  return (
    <div>
      <p className="text-[10px] uppercase tracking-[0.8px] text-[var(--text-muted,#64748B)] mb-2">
        Percentile Table
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-[12px]">
          <thead>
            <tr className="border-b border-white/[0.06]">
              <th className="text-left py-2 pr-4 text-[10px] uppercase tracking-wide text-[var(--text-hint,#475569)] font-medium">
                Metric
              </th>
              {pctLabels.map(p => (
                <th
                  key={p}
                  className={cn(
                    'text-right py-2 px-3 text-[10px] uppercase tracking-wide font-medium',
                    p === 50
                      ? 'text-[var(--accent)]'
                      : 'text-[var(--text-hint,#475569)]',
                  )}
                >
                  P{p}{p === 50 ? ' (Median)' : ''}
                </th>
              ))}
              <th className="text-right py-2 px-3 text-[10px] uppercase tracking-wide text-[#A855F7] font-medium">
                Current
              </th>
            </tr>
          </thead>
          <tbody>
            {metrics.map(m => (
              <tr
                key={m.label}
                className="border-b border-white/[0.03] hover:bg-white/[0.02] transition-colors"
              >
                <td className="py-2 pr-4 text-[11px] text-[var(--text-secondary,#94A3B8)]">
                  {m.label}
                </td>
                {pctLabels.map(p => {
                  const val = m.dist.percentiles[p]
                  return (
                    <td
                      key={p}
                      className="text-right py-2 px-3 font-['JetBrains_Mono',monospace]"
                      style={{ color: valueColor(val) }}
                    >
                      {fmt(val)}
                    </td>
                  )
                })}
                <td
                  className="text-right py-2 px-3 font-['JetBrains_Mono',monospace]"
                  style={{ color: '#A855F7' }}
                >
                  {fmt(m.det)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
